import { PureComponent } from 'react';
import autoBind from 'react-autobind';
import SortBy from 'lodash-es/sortBy';
import { InView } from 'react-intersection-observer';

import HPService from '../../services/HPService';
import SlideComponent from './SlideComponent';
import Slider from '../Shared/ReactSlick';
import CustomTweetsComponent from './CustomTweetsComponent';
import LogoComponent from './LogoComponent';
import { NextArrow, PrevArrow } from './CustomCarouselControls';
import SpinnerLoaderComponent from '../SpinnerLoaderComponent';
import withComponentName from '../../decorators/withComponentName';

import Styles from './styles.module.scss';

export class CustomCarousel extends PureComponent {
  constructor(props) {
    super(props);
    autoBind(this);
    this.timer = 0;
    this.state = {
      data: props.data,
      inView: false,
    };
  }

  async componentDidMount() {
    const { url, data } = this.props;
    if (!url && data) {
      this.timer = setTimeout(() => {
        this.setState({
          data,
        });
      }, 0);
    }
  }

  async componentDidUpdate(prevProps, prevState) {
    const { width, url, data } = this.props;
    const { inView } = this.state;

    if (inView) {
      if (
        prevState.inView !== inView
        || width !== prevProps.width
      ) {
        if (url) {
          this.setDataToState(await HPService.getCarouselData(url));
        } else if (data) {
          this.timer = setTimeout(() => {
            this.setState({
              data,
            });
          }, 0);
        }
      }
    }
  }

  componentWillUnmount() {
    const { url } = this.props;

    if (url) HPService.cancelCarouselRequest();

    if (this.timer) clearTimeout(this.timer);
  }

  onObserverChange(inView) {
    this.setState({ inView });
  }

  setDataToState(data) {
    if (data === null) return;

    const { section } = this.props;
    this.setState({
      data: section === 'testimonials' ? SortBy(data, 'id') : data,
    });
  }

  setCenterPadding(width) {
    if (width !== 0 && width <= 1024) return '20%';
    return '500px';
  }

  setSlideToScroll(width) {
    const { section, amountInSlide } = this.props;

    if (width !== 0 && width <= 1024 && width > 700) return section === 'testimonials' ? 1 : amountInSlide;

    return amountInSlide;
  }

  addSlideToCarousel(items) {
    return items.map((item) => (
      <div key={item}>
        {item}
      </div>
    ));
  }

  buildCarousel() {
    const { data } = this.state;
    const { section = '', category } = this.props;

    let slideItems = [];
    if (section === 'testimonials') {
      slideItems = data.map((item) => (
        <CustomTweetsComponent
          next={this.next}
          prev={this.previous}
          url={item.url}
          key={item.url}
          item={item.embedded_code}
        />
      ));
    } else if (section.indexOf('logos') !== -1) {
      const isMemberSection = (section.indexOf('members') !== -1);
      slideItems = data.map(({
        logo,
        id,
        width,
        height,
      }) => (
        <LogoComponent
          members={isMemberSection}
          src={logo}
          width={width}
          height={height}
          key={id}
          category={category}
        />
      ));
    } else {
      slideItems = data.map((item) => <SlideComponent item={item} key={item.id} />);
    }
    return this.addSlideToCarousel(slideItems);
  }

  isVariableWidth(width, section) {
    if (section === 'logos') return !(width !== 0 && width <= 1024);
    return false;
  }

  render() {
    const {
      amountInSlide, section, width, rows, customClass,
    } = this.props;
    const { data, inView } = this.state;

    const settings = {
      rows: rows || 1,
      centerPadding: this.setCenterPadding(width),
      dots: true,
      infinite: false,
      speed: 500,
      centerMode: false,
      slidesToShow: amountInSlide,
      slidesToScroll: this.setSlideToScroll(width, section),
      nextArrow: <NextArrow section={section} />,
      prevArrow: <PrevArrow section={section} />,
      appendDots: (dots) => (
        <div>
          <ul className="dots">{dots}</ul>
        </div>
      ),
      initialSlide: 0,
      variableWidth: this.isVariableWidth(width, section),
    };

    let carouselClass = '';
    switch (section) {
      case 'logos':
        carouselClass = 'custom_carousel for_logos';
        break;
      case 'testimonials':
        carouselClass = 'custom_carousel for_testimonials';
        break;
      case 'news_coverage':
        carouselClass = 'custom_carousel coverage';
        break;
      case 'news_coverage_home':
        carouselClass = 'custom_carousel coverage_home';
        break;
      default:
        carouselClass = 'custom_carousel';
        break;
    }

    return (
      <InView
        as="div"
        className={`${carouselClass} ${customClass}`}
        onChange={this.onObserverChange}
        skip={inView}
      >
        {(data && (
          <Slider {...settings}>
            {this.buildCarousel(data)}
          </Slider>
        )) || (
          <div className={Styles.spinner_container}>
            <SpinnerLoaderComponent />
          </div>
        )}
      </InView>
    );
  }
}

export default withComponentName(CustomCarousel);
